import React, { useState } from 'react';
import { IMilestoneCategory, IMilestoneData, MilestoneData } from '../api/getMilestone';
import { Checkbox } from 'antd';
import { MileStoneCard } from './MilestoneCard';
import { plusIcon } from './common/Icons';
import { IMilestoneHistory, IMilestoneStatus } from './common/types/milestones';

export const AgeGroupStrip: React.FC<{
    ageGroup: string;
    setSelectedMilestonesBulk: (
        val: IMilestoneData[],
    ) => {
        add: () => void;
        delete: () => void;
    };
    milestones: IMilestoneData[];
    selectedMilestones: IMilestoneData[];
    currAgeGroupIdx: number;
    index: number;
    achievedMilestonesLength: number;
}> = ({
    ageGroup,
    setSelectedMilestonesBulk,
    milestones,
    selectedMilestones,
    index,
    currAgeGroupIdx,
    achievedMilestonesLength,
}) => {
    const [checked, setChecked] = useState(false);

    const currMilestoneIds = milestones.map((m) => m.id);
    const currSelectedMilestones = selectedMilestones.filter((m) =>
        currMilestoneIds.includes(m.id),
    );

    return (
        <div className="flex justify-between w-full items-center bg-darwin-neutral-1000 rounded-8 px-16 py-12">
            <p className="text-white text-13 font-700 select-none">
                By {ageGroup}{' '}
                <span className="text-13 text-white font-700">
                    {`(${achievedMilestonesLength}/${milestones.length})`}
                </span>
            </p>

            <div className="flex items-center gap-12">
                {index === currAgeGroupIdx && (
                    <div className="px-8 select-none bg-darwin-accent-bright-yellow-800 text-12 flex justify-center items-center font-700 rounded-4">
                        CURRENT AGE
                    </div>
                )}
                <Checkbox
                    className="agegroup-checkbox"
                    checked={
                        selectedMilestones.length
                            ? milestones.length === currSelectedMilestones.length
                                ? checked
                                : false
                            : false
                    }
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedMilestonesBulk(milestones).add();
                        } else {
                            setSelectedMilestonesBulk(milestones).delete();
                        }

                        setChecked(e.target.checked);
                    }}
                />
            </div>
        </div>
    );
};

const MilestoneList: React.FC<{
    data: MilestoneData;
    ageGroup: string;
    selectedMilestones: IMilestoneData[];
    setSelectedMilestones: (
        val: IMilestoneData,
    ) => {
        add: () => void;
        delete: () => void;
        toggle: () => void;
    };
    setSelectedMilestonesBulk: (
        val: IMilestoneData[],
    ) => {
        add: () => void;
        delete: () => void;
    };
    age:
        | {
              value: string;
              unit: string;
          }
        | undefined;
    currAgeGroupIdx: number;
    index: number;
    markedMilestones: IMilestoneHistory[];
    saveMileStoneBulk: (result: IMilestoneHistory[]) => void;
    saveMileStone: (status: IMilestoneHistory) => void;
    openModalForEdit: (milestoneData: IMilestoneData) => void;
    markPreviousMilestonesAsAchieved: (
        status: IMilestoneStatus,
        categoryId: IMilestoneCategory,
        ageGroup?: string,
        markprev?: boolean,
    ) => void;
    categoryMap: Record<string, boolean>;
    setCurrAddMilestoneStateCallback: (value?: {
        ageGroup: {
            age: string;
            maxHrsFromBirth: number;
        };
    }) => void;
    hrsFromBirth: number;
    deleteMilestoneCallback: (milestone: IMilestoneData) => Promise<void>;
    clearMilestonesCallback: (clearedMilestones?: IMilestoneHistory[]) => void;
    getAllPrevMilestones: (
        categoryId?: IMilestoneCategory,
        ageGroup?: string,
        markprev?: boolean,
    ) => IMilestoneData[] | undefined;
    getMilestoneSummary: (
        milestonesTillCurrAge: IMilestoneData[],
        markedMilestonesAll: IMilestoneHistory[],
    ) => {
        result?: any;
        statuses: IMilestoneHistory[];
    };
}> = ({
    data,
    ageGroup,
    setSelectedMilestones,
    selectedMilestones,
    setSelectedMilestonesBulk,
    age,
    currAgeGroupIdx,
    index,
    markedMilestones,
    saveMileStone,
    openModalForEdit,
    markPreviousMilestonesAsAchieved,
    categoryMap,
    setCurrAddMilestoneStateCallback,
    hrsFromBirth,
    deleteMilestoneCallback,
    clearMilestonesCallback,
    getAllPrevMilestones,
    getMilestoneSummary,
}) => {
    const milestoneData = data.data;
    const categories = Object.keys(milestoneData);

    const milestones = categories.reduce((prev, curr) => {
        const currCat = milestoneData[curr];
        const currCatMilestones = Object.values(currCat);
        prev = [...prev, ...currCatMilestones];
        return prev;
    }, [] as IMilestoneData[]);

    const milestoneFinal = [
        ...(milestones.filter((m) => !m.id.includes('locale-')) ?? []),
        ...(milestones.filter((m) => m.id.includes('locale-')) ?? []).sort((a, b) => {
            const date1 = new Date(a.updated_at!);
            const date2 = new Date(b.updated_at!);
            return date1 > date2 ? 1 : date1 < date2 ? -1 : 0;
        }),
    ];

    const achievedMilestones = markedMilestones.filter(
        (m) =>
            milestoneFinal.map((val) => val.id).includes(m.id) &&
            (m.status === 'achieved' ||
                m.status === 'achieved_delay' ||
                m.status === 'achieved_early' ||
                m.status === 'achieved_unknown'),
    );

    return (
        <div className="flex min-w-300 max-w-300 w-300 flex-shrink-0 overflow-auto hide-scrollbar flex-col gap-8">
            <AgeGroupStrip
                ageGroup={ageGroup}
                setSelectedMilestonesBulk={setSelectedMilestonesBulk}
                milestones={milestoneFinal}
                selectedMilestones={selectedMilestones}
                currAgeGroupIdx={currAgeGroupIdx}
                index={index}
                achievedMilestonesLength={achievedMilestones.length}
            />
            <div className="flex w-full overflow-auto hide-scrollbar flex-col gap-8">
                {milestoneFinal.map((m, idx) => {
                    const status = markedMilestones.find((val) => val.id === m.id)?.status;

                    return (
                        <MileStoneCard
                            key={m.id}
                            milestoneData={m}
                            index={idx}
                            ageGrpIdx={index}
                            setSelectedMilestones={setSelectedMilestones}
                            selectedMilestones={selectedMilestones}
                            status={status}
                            saveMileStone={saveMileStone}
                            age={age}
                            openModalForEdit={openModalForEdit}
                            markPreviousMilestonesAsAchieved={markPreviousMilestonesAsAchieved}
                            categoryMap={categoryMap}
                            deleteMilestoneCallback={deleteMilestoneCallback}
                            clearMilestonesCallback={clearMilestonesCallback}
                            getMilestoneSummary={getMilestoneSummary}
                            getAllPrevMilestones={getAllPrevMilestones}
                            markedMilestones={markedMilestones}
                        />
                    );
                })}
                {/* sticky bottom-0 */}
                <div
                    onClick={() =>
                        setCurrAddMilestoneStateCallback({
                            ageGroup: { age: ageGroup, maxHrsFromBirth: hrsFromBirth },
                        })
                    }
                    id={`add-milestone-${ageGroup}`}
                    className="flex items-center justify-start p-8 cursor-pointer rounded-8 hover:bg-darwin-accent-bright-blue-100"
                >
                    <p className="flex text-12 font-700 text-darwin-accent-bright-blue-800 items-center gap-4">
                        <span>{plusIcon('w-11 h-11 fill-darwin-accent-bright-blue-800')}</span>
                        Add milestone
                    </p>
                </div>
            </div>
        </div>
    );
};

export default MilestoneList;
